import React, { useEffect, useState } from 'react';
import { Typography, Box, Container, Fab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useRecipe } from '../../context/RecipeContext';
import { JournalEntry } from '../../types/journal';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import JournalItem from './JournalItem';
import { track } from '@/third-party/MixPanel';

interface TimelineItem {
    id: string;
    createdAt: Date;
    data: JournalEntry;
}

const JournalPage: React.FC = () => {
    const { t } = useTranslation();
    const { journals: contextJournals, getJournals } = useRecipe();
    const navigate = useNavigate();

    // Initialize state with localStorage values as defaults
    const [journals, setJournals] = useState<JournalEntry[]>(() => {
        const stored = localStorage.getItem('journals');
        return stored ? JSON.parse(stored) : [];
    });

    useEffect(() => {
        // Update state with context data when it becomes available
        if (contextJournals && Array.isArray(contextJournals) && contextJournals.length > 0) {
            setJournals(contextJournals);
        }
    }, [contextJournals]);

    useEffect(() => {
        // Always fetch fresh data in the background when the page loads
        getJournals();
    }, []);


    // Get sorted journal entries by date
    const getTimelineItems = (): TimelineItem[] => {
        const items: TimelineItem[] = [];

        if (Array.isArray(journals)) {
            journals.forEach((entry) => {
                items.push({
                    id: entry._id,
                    createdAt: new Date(entry.createdAt),
                    data: entry
                });
            });
        }

        // Sort by date (newest first)
        return items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    };

    const timelineItems = getTimelineItems();

    // Group items by month
    const groupedItems = timelineItems.reduce((acc: Record<string, TimelineItem[]>, item) => {
        const locale = i18next.language === 'pt-BR' ? ptBR : enUS;
        const monthYear = format(item.createdAt, 'MMMM yyyy', { locale });

        if (!acc[monthYear]) {
            acc[monthYear] = [];
        }
        acc[monthYear].push(item);
        return acc;
    }, {});

    const handleAddJournal = () => {
        track('journal_started');
        navigate('/journal/new');
    };

    return (
        <Box sx={{ minHeight: '100vh', overflow: 'hidden', paddingTop: '20px' }}>
            <Box sx={{
                bgcolor: '#F5F3F0',
                color: '#42403C',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: 0,
                    height: 0,
                    background: 'transparent'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}>
                <Container sx={{
                    overflow: 'auto',
                    padding: 0,
                    maxWidth: '100%',
                    px: { xs: 2, sm: 3, md: 4 }
                }}>
                    {Object.entries(groupedItems).map(([monthYear, monthItems]) => (
                        <Box key={monthYear} sx={{ mb: 0 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    padding: { xs: 2, sm: 3 },
                                    paddingBottom: 2,
                                    paddingTop: 3,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    fontFamily: '"Playfair Display", serif',
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    borderBottom: '1px solid',
                                    borderColor: 'rgba(0,0,0,0.08)',
                                    mb: 2
                                }}
                            >
                                {monthYear}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0,
                                px: { xs: 1, sm: 2 }
                            }}>
                                {monthItems.map((item, index) => (
                                    <Box key={item.id}>
                                        <JournalItem
                                            entry={item.data}
                                            index={index}
                                            totalEntries={monthItems.length}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}

                    {Object.keys(groupedItems).length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60vh',
                            textAlign: 'center',
                            px: 3
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#999',
                                    fontFamily: '"Playfair Display", serif',
                                    mb: 2
                                }}
                            >
                                {t('journal.noEntriesYet')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#BBB',
                                    maxWidth: '300px'
                                }}
                            >
                                {t('journal.startJourney')}
                            </Typography>
                        </Box>
                    )}
                </Container>
            </Box>

            <Fab
                color="primary"
                aria-label={t('journal.addEntry')}
                onClick={handleAddJournal}
                sx={{
                    position: 'fixed',
                    bottom: 80,
                    right: 16,
                    backgroundColor: '#2B2E4A',
                    boxShadow: '0 4px 20px rgba(43, 46, 74, 0.3)',
                    '&:hover': {
                        backgroundColor: '#1F2238',
                        boxShadow: '0 6px 25px rgba(43, 46, 74, 0.4)',
                        transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <AddIcon sx={{ fontSize: 24 }} />
            </Fab>
        </Box>
    );
};

export default JournalPage;